import { useEffect, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "@core/components/ui/Dialog";
import Heading from "@core/components/title/Heading";
import BulletList from "@core/components/list/BulletList";
import { CheckboxGroup } from "@core/components/ui/Checkbox";
import { Button } from "@core/components/ui";
import Spinner from "@core/components/loading/Spinner";
import CustomAlert from "@core/components/dialog/CustomAlert";
import { usePartnerApply } from "@features/auth/hooks/mutations/usePartnerApply";
import { useDialog } from "@core/components/dialog/hooks/useDialog";
import { loginQueryKeys } from "@features/auth/hooks/queries/keys/loginQueryKeys";
import { format } from "date-fns";
import { cn } from "@core/utils/domUtil";

function PartnerPcbang({ registerDate }: { registerDate?: string }) {
  return (
    <>
      {registerDate && <div className="text-point-600 text-md mx-auto mt-[20px] inline-flex items-center justify-center bg-purple-100 px-[20px] py-[10px]">파트너 PC방 가입 일자: {format(new Date(registerDate), "yyyy-MM-dd")}</div>}
      <DialogFooter>
        <DialogClose asChild>
          <Button variant="bgPoint600" size="large">
            확인
          </Button>
        </DialogClose>
      </DialogFooter>
    </>
  );
}

function NotPartnerPcbang({ iCafeId, onOpenChange, setIsModalPending }: { iCafeId: string; onOpenChange: (open: boolean) => void; setIsModalPending: (isModalPending: boolean) => void }) {
  const queryClient = useQueryClient();
  const { isOpen: alertIsOpen, toggle: alertToggle, open: alertOpen } = useDialog({ type: "dialog", name: "partnerAgreeCompleteAlert" });
  const [agreeChecked, setAgreeChecked] = useState(false);
  const [agreeErrorMsg, setAgreeErrorMsg] = useState("");

  const { mutate: partnerApply, isPending: isPartnerApplyPending, isSuccess: isPartnerApplySuccess } = usePartnerApply(iCafeId);

  // 파트너 PC방 가입 신청 성공 시 모달 닫기
  useEffect(() => {
    if (isPartnerApplySuccess) {
      alertOpen();
      setIsModalPending(false);
    }
  }, [isPartnerApplySuccess]);

  // mutate pending 상태일 때 모달 로딩 중으로 변경
  useEffect(() => {
    if (isPartnerApplyPending) {
      setIsModalPending(true);
    }
  }, [isPartnerApplyPending]);

  const clickHandler = useCallback(() => {
    if (!agreeChecked) {
      setAgreeErrorMsg("이용약관에 동의해주세요.");
    } else {
      if (iCafeId) {
        partnerApply(); // 파트너 PC방 가입 신청
        setAgreeErrorMsg("");
        return;
      }

      setAgreeErrorMsg("대표 PC방을 선택해주세요.");
    }
  }, [agreeChecked, iCafeId, setAgreeErrorMsg, partnerApply]);

  const closeAlertHandler = useCallback(() => {
    // Apply Dialog 닫기
    onOpenChange(false);

    // alert 닫으면 loginICafeInfo 캐시 무효화 및 refetch
    queryClient.refetchQueries({ queryKey: loginQueryKeys.loginICafeInfo.info(iCafeId)?.queryKey });
  }, [iCafeId, queryClient]);

  return (
    <>
      <div className="flex flex-col items-center justify-center">
        <CheckboxGroup checked={agreeChecked} onChange={setAgreeChecked} id="argeeTerms" label="위 이용약관에 동의합니다." />
        {agreeErrorMsg && <p className="text-error text-sm">{agreeErrorMsg}</p>}
      </div>
      <DialogFooter>
        <DialogClose asChild>
          <Button variant="borderBgWhite" size="large">
            취소
          </Button>
        </DialogClose>
        <Button variant="bgPoint600" size="large" onClick={clickHandler} disabled={!agreeChecked}>
          동의
        </Button>
      </DialogFooter>
      <CustomAlert name="partnerAgreeCompleteAlert" isOpen={alertIsOpen} onOpenChange={alertToggle} onClick={closeAlertHandler}>
        가입이 완료되었습니다.
      </CustomAlert>
    </>
  );
}

export default function LayerPartnerAgree({ open, onOpenChange, iCafeId, registerDate, isPartner }: { open: boolean; onOpenChange: (open: boolean) => void; iCafeId?: string; registerDate?: string; isPartner: boolean }) {
  const [isTermsLoading, setIsTermsLoading] = useState(true);
  const [isModalPending, setIsModalPending] = useState(false);

  useEffect(() => {
    if (open) {
      setIsTermsLoading(true);
    }
  }, [open]);

  const loadHandler = useCallback(() => {
    setIsTermsLoading(false);
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="lg:w-full lg:max-w-[600px]" isLoading={isModalPending} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>파트너 PC방 약관 동의</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-[20px]">
          <Heading level={2} className="text-md!">
            이용약관
          </Heading>
          <BulletList>
            <li>스마트 PC방 이용약관은 NCSOFT 가맹 서비스 이용약관의 특별약관입니다.</li>
            <li>NC 스마트 PC방 서비스를 이용하기 위해서는 아래 이용약관에 동의해야 합니다.</li>
            <li>이용약관에 동의하기 전 반드시 모든 내용을 확인하시기 바랍니다.</li>
          </BulletList>
          <div className={cn("relative h-[270px] overflow-hidden border border-gray-300")}>
            {isTermsLoading && <Spinner className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />}
            <iframe width="100%" height="100%" src="https://www.plaync.com/policy/api/view/smart_service" onLoad={loadHandler} title="이용약관" />
          </div>
          {isPartner ? <PartnerPcbang registerDate={registerDate} /> : iCafeId && <NotPartnerPcbang iCafeId={iCafeId} onOpenChange={onOpenChange} setIsModalPending={setIsModalPending} />}
        </div>
      </DialogContent>
    </Dialog>
  );
}
